import {
    Link,
    Navigate,
    Outlet,
    type RouteObject,
    useNavigate,
    useParams,
} from "react-router-dom";

import Background from "@app/components/Background/index.tsx";
import { SupNavbar } from "@app/screens/Global/SupNavbar/index.tsx";
import TopBar from "@app/screens/Global/TopBar/index.tsx";
import { Questionnaire, Questionnaires, Sup, Users } from "@app/screens/Sup";
import ErrorBoundary from "@app/shared/components/ErrorBoundary/index.tsx";
import { isNavbarOpenAtom } from "@app/store/navbarStore.ts";
import { cn } from "@design-system/Utilities/index.tsx";
import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { UserRole } from "../../generated/client/graphql.ts";
import Loading from "../components/Loading/index.tsx";
import {
    CustomerChurnedErrorPage,
    GeneralErrorPage,
    InternalServerErrorPage,
} from "./admin.tsx";
import {
    adminPaths,
    supplierPaths as adminSupplierPaths,
} from "./adminPaths.tsx";
import { useSupplierUsers, useUserFirstSupplier } from "./data.ts";
import { supPaths } from "./supPaths.tsx";
import { makeAbsoluteUrl, useNonPublicRoutes } from "./utils.ts";

export const SupLayout = () => {
    const {
        copyErrorToClipboard,
        currentUserRole,
        currentUserId,
        error,
        goToHomepage,
        handleChurnedErrorPageCTA,
        isLoading,
        isRefetching,
        orgIsLoading,
        showCustomerChurnedErrorPage,
        showInternalServerErrorPage,
    } = useNonPublicRoutes();

    const { sup_id } = useParams();

    const { supplierUsers } = useSupplierUsers(sup_id);
    useSupplierRedirectUrl({
        userId: currentUserId,
        role: currentUserRole,
    });

    const isCurrentUserSupplier =
        sup_id &&
        currentUserRole === UserRole.Supplier &&
        supplierUsers?.some((user) => user.id === currentUserId);

    const isNavbarOpen = useAtomValue(isNavbarOpenAtom);

    if (showCustomerChurnedErrorPage) {
        return (
            <CustomerChurnedErrorPage onCTAClick={handleChurnedErrorPageCTA} />
        );
    }

    if (isLoading || isRefetching || orgIsLoading) {
        return <Loading size="large" />;
    }

    if (
        (currentUserRole &&
            [UserRole.Admin, UserRole.BeavrAdmin].includes(currentUserRole)) ||
        isCurrentUserSupplier
    ) {
        return (
            <div className="min-h-screen antialiased flex">
                <div
                    className={cn(
                        "transition-all duration-200 ease-in-out overflow-hidden",
                        isNavbarOpen
                            ? "w-[250px] min-w-[250px]"
                            : "w-0 min-w-0",
                    )}
                >
                    <SupNavbar />
                </div>
                <div className="min-h-screen w-full min-w-[960px]">
                    <Background topBar={<TopBar />}>
                        <ErrorBoundary>
                            <Outlet />
                        </ErrorBoundary>
                    </Background>
                </div>
            </div>
        );
    }

    if (showInternalServerErrorPage) {
        return <InternalServerErrorPage onCTAClick={goToHomepage} />;
    }

    if (error) {
        return <GeneralErrorPage onCTAClick={copyErrorToClipboard} />;
    }

    return <></>;
};

function useSupplierRedirectUrl({
    userId,
    role,
}: {
    userId?: string;
    role?: UserRole | null;
}) {
    const { org_uname, sup_id } = useParams();

    const navigate = useNavigate();

    const isSupplier = role === UserRole.Supplier;
    const { supplier, isLoading } = useUserFirstSupplier(
        isSupplier ? userId : undefined,
    );

    useEffect(() => {
        if (sup_id || !userId || !role || isLoading) {
            return;
        }

        if (!isSupplier && !sup_id) {
            window.location.href = makeAbsoluteUrl(
                `o/${org_uname}/admin/${adminPaths.supplier}/${adminSupplierPaths.suppliers}`,
            );
        }

        if (isSupplier && !sup_id && supplier) {
            navigate(supplier.id);
        }
    }, [
        sup_id,
        userId,
        role,
        isLoading,
        navigate,
        org_uname,
        isSupplier,
        supplier,
    ]);
}

export const supRoutes: RouteObject[] = [
    {
        index: true,
        element: <Sup />,
    },
    {
        path: supPaths.sup_id_param,
        element: <Outlet />,
        children: [
            {
                index: true,
                element: <Navigate to={supPaths.questionnaires} replace />,
            },
            {
                path: supPaths.questionnaires,
                handle: {
                    crumb: Link,
                    page: supPaths.questionnaires,
                },
                children: [
                    {
                        index: true,
                        element: <Questionnaires />,
                    },
                    {
                        path: supPaths.questionnaire_id_param,
                        element: <Questionnaire />,
                        handle: {
                            crumb: Link,
                            page: supPaths.questionnaire_id_param,
                        },
                    },
                ],
            },
            {
                path: supPaths.users,
                element: <Users />,
            },
        ],
    },
];
